import $ from 'jquery';
import { getElement } from './dom.js';

/**
 * @param {any} obj
 */
export const filterEmptyKeys = obj => Object.fromEntries(Object.entries(obj).filter(([_, v]) => v != null));

/**
 * @param {Set} a
 * @param {Set} b
 */
export const setIntersect = (a, b) => new Set([...a].filter(x => b.has(x)));

/**
 * @param {Set} a
 * @param {Set} b
 */
export const setDiff = (a, b) => new Set([...a].filter(x => !b.has(x)));

/**
 * @param {Map} a
 * @param {Map} b
 */
export const mapIntersectKey = (a, b) => new Map([...a].filter(([k]) => b.has(k)));

/**
 * @param {Map} a
 * @param {Map} b
 */
export const mapIntersect = (a, b) => new Map([...a].filter(([k, v]) => b.has(k) && b.get(k) === v));

/**
 * @param {Map} a
 * @param {Map} b
 */
export const mapDiffKey = (a, b) => new Map([...a].filter(([k]) => !b.has(k)));

/**
 * @param {Map} a
 * @param {Map} b
 */
export const mapDiff = (a, b) => new Map([...a].filter(([k, v]) => !b.has(k) || b.get(k) !== v));

/**
 * @param {MpDataAction} action
 * @return {[string,any[]]}
 */
export const parseDataAction = (action) => {

	// Simple action string
	if (typeof action === 'string') {
		return [action, []];
	}

	try {
		if (!action || !(typeof action === 'object')) {
			throw new Error('INVALID_MP_DATA_ACTION');
		}

		// Using the new action object format ({ name: ..., args: ... })
		if (('name' in action) && (typeof action.name === 'string')) {
			const actionArgs = action.args || [];

			if (!Array.isArray(actionArgs)) {
				throw new TypeError('INVALID_MP_DATA_ACTION_ARGS');
			}

			return [action.name, actionArgs];
		}

		// Using the deprecated format ({ action: args[] })
		const actionEntries = Object.entries(action);

		// Must have exactly one entry (one action/args pair)
		if (actionEntries.length !== 1) {
			throw new Error('INVALID_MP_DATA_ACTION_ENTRY_LENGTH');
		}

		// Extract pair
		const [actionName, actionArgs] = actionEntries[0];

		if (!Array.isArray(actionArgs)) {
			throw new TypeError('INVALID_MP_DATA_ACTION_ARGS');
		}

		// Default to no args
		return [actionName, actionArgs];
	}
	catch (/** @type {any} */ error) {
		console.warn('Invalid action: (%o)', action, error.message);
		return ['', []];
	}
};

/**
 * Diff 2 FormData objects and return true if they are equal
 *
 * @param {FormData} formData1
 * @param {FormData} formData2
 */
export const formDataCompare = (formData1, formData2) => {

	const keys1 = [...formData1.keys()];
	const keys2 = [...formData2.keys()];

	// Check if both FormData objects have the same number of keys
	if (keys1.length !== keys2.length) {
		return false;
	}

	// Iterate over the keys of the first FormData object
	for (const key of keys1) {
		// Check if the second FormData object has the same key
		if (!formData2.has(key)) {
			return false;
		}

		// Get the values for the current key from both FormData objects
		const values1 = formData1.getAll(key);
		const values2 = formData2.getAll(key);

		// Check if both arrays of values have the same length
		if (values1.length !== values2.length) {
			return false;
		}

		// Check if all values in the first array are present in the second array
		for (const value of values1) {

			// Empty file automatically added by the browser when a file input is present and no file is selected
			if ((value instanceof Blob) && (value.size === 0) && !value.name) {
				continue;
			}

			if (!values2.includes(value)) {
				return false;
			}
		}
	}

	// If all checks passed, return true
	return true;
};

/**
 * Diff 2 FormData objects and return the difference
 *
 * @param {FormData} formData1
 * @param {FormData} formData2
 */
export const formDataDiff = (formData1, formData2) => {
	const result = new FormData();

	// Iterate over the keys of the first FormData object
	for (const key of formData1.keys()) {
		// Check if the second FormData object has the same key
		if (!formData2.has(key)) {
			// Add all values for this key to the result
			for (const value of formData1.getAll(key)) {
				result.append(key, value);
			}
		}
		else {
			// Get the values for the current key from both FormData objects
			const values1 = formData1.getAll(key);
			const values2 = formData2.getAll(key);

			// Check if all values in the first array are present in the second array
			for (const value of values1) {

				// Empty file automatically added by the browser when a file input is present and no file is selected
				if ((value instanceof Blob) && (value.size === 0) && !value.name) {
					continue;
				}

				if (!values2.includes(value)) {
					result.append(key, value);
				}
			}
		}
	}

	return result;
};

/**
 * @param {*} iterable
 */
export const iterableToJson = (iterable) => {

	const json = Object.create(null);

	for (const [key, value] of iterable) {
		json[key] = value;
	}

	return json;
};
/**
 * @param {string | HTMLElement | JQuery} selector
 * @param {string=} entryName
 * @param {FormData=} formData
 */
export const getFormDataForElement = (selector, entryName, formData) => {

	if (!formData) {
		formData = new FormData();
	}

	/** @type {HTMLFormElement | HTMLInputElement} */ // @ts-ignore
	const element = getElement(selector);

	if (!element) {
		console.warn('getFormDataForElement: Element not found', selector);
		return formData;
	}

	// Form
	if (element instanceof HTMLFormElement) {
		const currentFormData = new FormData(element);
		for (const [key, value] of currentFormData) {
			const dataKey = (entryName ? prependFormNameParts(entryName, key) : key);
			formData.append(dataKey, value ?? '');
		}
	}
	// Get element data from parent form
	else if (element.form) {
		const parentFormData = new FormData(element.form);
		const elementData = parentFormData.getAll(element.name);
		const dataKey = (entryName || element.name);

		// Element has multiple values
		if (elementData.length > 1) {
			for (const value of elementData) {
				formData.append(dataKey, value ?? '');
			}
		}
		// Element has a single value
		else {
			formData.set(dataKey, elementData[0] ?? '');
		}
	}
	// Get element data from element itself
	else {
		const dataKey = (entryName || element.name);
		let $element = $(element);
		if ($element.is('input,select,textarea') === false) {
			// @ts-ignore
			$element = $element.find('input,select,textarea');
		}
		formData.set(dataKey, $element.serializeArray()?.[0]?.value ?? '');
	}

	return formData;
};

/**
 * @param {string} name
 */
const getArrayNameParts = (name) => {
	/** @type {string[]} */
	const parts = [];
	name = name.replaceAll(/\[([^\]]*)]/g, (_, g1) => {
		parts.push(g1);
		return '';
	});
	return {
		name,
		parts,
	};
};

/**
 * @param {string} newName
 * @param {string} oldName
 */
const prependFormNameParts = (newName, oldName) => {
	const { name, parts } = getArrayNameParts(oldName);
	return parts.length > 0 ? `${newName}[${name}][${parts.join('][')}]` : `${newName}[${name}]`;
};